import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import PageContainer from '../layout/PageContainer';
import { Alert, Divider } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  gridClasses
} from '@mui/x-data-grid';
import type { Customer } from '../../models/Customer';
import { GET_CUSTOMER_BY_ID_QUERY } from '../../queries/CustomerQueries';
import { GET_TOTAL_ACTIVE_ANNUAL_OVERHEAD_COST } from '../../queries/OverheadCostLineItemQueries';
import { GET_TOTAL_CONSUMABLE_COST } from '../../queries/ConsumableLineItemQueries';
import type { Product } from '../../models/Product';
import { toUSCurrency } from '../../utilities/CurrencyUtilities';

const INITIAL_PAGE_SIZE = 100;

export default function CustomerBulkCostingPriceList() {
  const { customerId } = useParams();
  const navigate = useNavigate();

  const [getCustomerById] = useLazyQuery(
    GET_CUSTOMER_BY_ID_QUERY);

  const { data: totalActiveAnnualOverheadCostData } = useQuery(
    GET_TOTAL_ACTIVE_ANNUAL_OVERHEAD_COST);

  const { data: totalConsumableCostData } = useQuery(
    GET_TOTAL_CONSUMABLE_COST);

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const totalOverheadCostPerPound = useMemo(() => {
    return (totalActiveAnnualOverheadCostData?.totalActiveAnnualOverheadCost || 0.00) / 2000;
  }, [totalActiveAnnualOverheadCostData]);

  const totalLaborCostPerPound = useCallback((amount: number) => {
    return ((amount / 14) * 40.0) / amount;
  }, []);

  const totalConsumableCostPerPound = useCallback((amount: number) => {
    return (totalConsumableCostData?.totalConsumableCost || 0.00) / amount;
  }, [totalConsumableCostData?.totalConsumableCost]);

  const totalNonIngredientCostPerPound = useCallback((amount: number) => {
    return totalOverheadCostPerPound + totalLaborCostPerPound(amount) + totalConsumableCostPerPound(amount);
  }, [totalOverheadCostPerPound, totalLaborCostPerPound, totalConsumableCostPerPound]);

  const totalCostPerPound = useCallback((totalIngredientCostPerPound: number, amount: number) => {
    return totalIngredientCostPerPound + totalNonIngredientCostPerPound(amount) + totalOverheadCostPerPound + totalLaborCostPerPound(amount) + totalConsumableCostPerPound(amount);
  }, [totalNonIngredientCostPerPound, totalOverheadCostPerPound, totalLaborCostPerPound, totalConsumableCostPerPound]);

  const [rowsState, setRowsState] = useState<{
    rows: Product[];
    rowCount: number;
  }>({
    rows: [],
    rowCount: 0,
  });

  const loadData = useCallback(async () => {
    setError(null);

    const id = +(customerId || '0');

    if (id > 0) {
      setIsLoading(true);

      try {
        const { data, error: getCustomerByIdError } = await getCustomerById(
          { variables: { id } }
        );

        setIsLoading(false);
        setError(null);

        if (getCustomerByIdError) {
          setError(getCustomerByIdError);
        }

        if (data?.customer) {
          setCustomer(data?.customer);

          setRowsState({
            rows: data?.customer?.bulkPricingProducts || [],
            rowCount: (data?.customer?.bulkPricingProducts || []).length || 0,
          });
        } else {
          setError({ message: 'No customer found' } as Error);
        }
      } catch (showDataError) {
        setError(showDataError as Error);
      }
    }
    setIsLoading(false);
  }, [customerId, getCustomerById]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleBack = useCallback(() => {
    navigate(`/dashboard/customers/${customerId}/bulk-costing`);
  }, [customerId, navigate]);

  const initialState = useMemo(
    () => ({
      pagination: { paginationModel: { pageSize: INITIAL_PAGE_SIZE } },
    }),
    [],
  );

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'name',
        headerName: 'Recipe',
        display: 'flex',
        flex: 1.5
      },
      {
        field: 'cost100PoundsAndUp',
        headerName: '100 POUNDS & UP',
        type: 'number',
        display: 'flex',
        flex: 1.25,
        valueGetter: (_, row) => {
          const product = row as Product;

          if ((product.bulkPricingMarkupLineItems || []).length === 3) {
            const bulkPricingMarkupLineItem = (product.bulkPricingMarkupLineItems || [])[2];

            return `$${toUSCurrency(totalCostPerPound(product.totalAverageItemCostPerBatchPound || 0, bulkPricingMarkupLineItem.pounds) * (bulkPricingMarkupLineItem.markup / 100))}`
          }
        }
      },
      {
        field: 'cost50_99Pounds',
        headerName: '50 - 99 POUNDS',
        type: 'number',
        display: 'flex',
        flex: 1.25,
        valueGetter: (_, row) => {
          const product = row as Product;

          if ((product.bulkPricingMarkupLineItems || []).length === 3) {
            const bulkPricingMarkupLineItem = (product.bulkPricingMarkupLineItems || [])[1];

            return `$${toUSCurrency(totalCostPerPound(product.totalAverageItemCostPerBatchPound || 0, bulkPricingMarkupLineItem.pounds) * (bulkPricingMarkupLineItem.markup / 100))}`
          }
        }
      },
      {
        field: 'cost25_49Pounds',
        headerName: '25 - 49 POUNDS',
        type: 'number',
        display: 'flex',
        flex: 1.25,
        valueGetter: (_, row) => {
          const product = row as Product;

          if ((product.bulkPricingMarkupLineItems || []).length === 3) {
            const bulkPricingMarkupLineItem = (product.bulkPricingMarkupLineItems || [])[0];

            return `$${toUSCurrency(totalCostPerPound(product.totalAverageItemCostPerBatchPound || 0, bulkPricingMarkupLineItem.pounds) * (bulkPricingMarkupLineItem.markup / 100))}`
          }
        }
      }
    ],
    [totalCostPerPound],
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          m: 1,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <Alert severity="error">{error.message}</Alert>
      </Box>
    );
  }

  const pageTitle = `${customer?.name} Bulk Costing Price List`;

  return (
    <PageContainer
      title={pageTitle}
      breadcrumbs={[
        { title: 'Customers', path: '/dashboard/customers' },
        { title: `${customer?.name}`, path: `/dashboard/customers/${customer?.id}` },
        { title: `${customer?.name} Bulk Costing`, path: `/dashboard/customers/${customer?.id}/bulk-costing` },
        { title: 'Bulk Costing Price List' }
      ]}
    >
      <Box
        sx={{ width: '100%' }}
      >
        <Box sx={{ height: '50vh' }}>
          <DataGrid
            density="compact"
            rows={rowsState.rows}
            columns={columns}
            getRowId={(row) => row.id}
            pagination
            disableRowSelectionOnClick
            loading={isLoading}
            initialState={initialState}
            showToolbar
            pageSizeOptions={[INITIAL_PAGE_SIZE]}
            getRowHeight={() => 'auto'}
            sx={{
              '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '8px' },
              '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '15px' },
              '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '22px' },
              [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
                outline: 'transparent',
              },
              [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
              {
                outline: 'none',
              },
              [`& .${gridClasses.row}:hover`]: {
                cursor: 'pointer',
              }
            }}
            slotProps={{
              loadingOverlay: {
                variant: 'circular-progress',
                noRowsVariant: 'circular-progress',
              },
              baseIconButton: {
                size: 'small',
              },
            }}
          />
        </Box>
        <Divider sx={{ my: 3 }} />
        <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back
          </Button>
        </Stack>
      </Box >
    </PageContainer >
  );
}
